"use strict";

import bbox from "geojson-bbox";
import proj4 from "proj4";
import axios from "axios";
import uuid from "uuid";
import { fabric } from "fabric";

class InterpolatedLayer {
    constructor(settings) {
        this.id = uuid.v4();

        // Fabric objects
        this.imagesGroup = null;
        this.samples = null;
        this.polygonGroup = null;
        this.text = null;
        this.background = null;
        this.group = null;

        // Will contain each interpolated image, because of async issues it has to be global
        this.images = [];

        // Will contain this layers outer bounding box
        this.bbox = [];
        // Will contain features outer bounding box
        this.featureCollectionBBOX = [];

        // Will contain the features that this layer is based on
        this.featureCollection = null;

        // The needed adjustments (top, left) when changing interpolation type
        this.adjustments = null;

        // Settings
        this.settings = settings;
        this.textTop = 5;
        this.textLeft = 10;
        this.quality = 0.518264828;
        this.scaleBy = 1;

        this.loading = false;
    }

    /**
     * Create fabric groups that contains the polygons and text items
     *
     * @param {GeoJson} featureCollection
     */
    addFeatures(featureCollection, samples) {
        this.featureCollection = featureCollection;
        this.calculateBBOX(featureCollection);
        this.samples = this.parseSamples(samples);
        this.polygonGroup = this.createPolygons(featureCollection);
        this.text = this.createTextGroup();
    }

    /**
     *	Will append more data on each sample based on their location
     *	Only samples with matching coordinates and provnr in the data array will be updated
     *
     * @param {array} data
     */
    addExtraAnalysis(data) {
        proj4.defs(
            "EPSG:3021",
            "+proj=tmerc +lat_0=0 +lon_0=15.80827777777778 +k=1 +x_0=1500000 +y_0=0 +ellps=bessel +towgs84=414.1,41.3,603.1,-0.855,2.141,-7.023,0 +units=m +no_defs"
        );
        let searchRadius = 2.0;

        this.samples = this.samples.map(sample => {
            let coords = proj4("EPSG:3857", "EPSG:3021", [sample.x, sample.y]);

            let matchingNewData = data.find(newSampleData => {
                // Check if the x and y values of the samples is within 2 meters of one of the samples in the previous array
                return (
                    coords[0] >= newSampleData.x - searchRadius &&
                    coords[0] <= newSampleData.x + searchRadius &&
                    coords[1] >= newSampleData.y - searchRadius &&
                        coords[1] <= newSampleData.y + searchRadius &&
                    parseInt(newSampleData.provnr) === parseInt(sample.provnr)
                );
            });

            sample.kalk_50_procent_cao = parseFloat(
                matchingNewData.kalk_50_procent_cao
            );
            sample.avkastningsminskning = parseFloat(
                matchingNewData.avkastningsminskning
            );
            sample.magnesiumbehov = parseFloat(matchingNewData.magnesiumbehov);

            return sample;
        });
    }

    /**
     * Redraw this layer with new settings
     *
     * @param {Object} settings
     * @param {function} callback
     */
    redraw(settings, callback) {
        this.loading = true;
        this.settings = settings;
        this.images = [];

        let adjustPlacement = {
            top: this.group.top,
            left: this.group.left,
            scaleX: this.group.scaleX,
            scaleY: this.group.scaleY
        };

        let interpolationDone = () => {
            this.text = this.createTextGroup();

            this.polygonGroup.set({
                top: 0,
                left: 0
            });

            this.imagesGroup.set({
                top: 0,
                left: 0
            });

            let textAndPolygon = new fabric.Group([
                this.imagesGroup,
                this.text,
                this.polygonGroup
            ]);

            let layers = [textAndPolygon];

            if (this.background) {
                textAndPolygon.set({
                    top: this.adjustments.top,
                    left: this.adjustments.left,
                    scaleX: this.scaleBy,
                    scaleY: this.scaleBy
                });

                this.background.img.set({
                    top: 0,
                    left: 0
                });

                layers.unshift(this.background.img);
            }

            this.group = new fabric.Group(layers, adjustPlacement);
            this.group.id = this.id;
            this.loading = false;
            callback();
        };

        this.addInterpolations(interpolationDone);
    }

    /**
     * Fetch the background image that is contained by the GeoJson object
     *
     * @param {GeoJson} background
     */
    loadBackground(background) {
        this.bbox = bbox(background);
        let topLeft = [this.bbox[0], this.bbox[3]];
        let bottomRight = [this.bbox[2], this.bbox[1]];
        let requestParams = {
            polygon: JSON.stringify(background.geometry),
            x1: topLeft[0],
            y1: topLeft[1],
            x2: bottomRight[0],
            y2: bottomRight[1]
        };

        this.background = {
            props: {
                topLeft: topLeft,
                bottomRight: bottomRight
            },
            img: null
        };

        return new Promise((resolve, reject) => {
            axios
                .post("/print/intersecting/backgroundimage/crop", requestParams)
                .then(
                    response => {
                        if (response.data.length > 0) {
                            fabric.Image.fromURL(response.data, img => {
                                if (img.width > this.settings.maxWidth) {
                                    img.scaleToWidth(this.settings.maxWidth);
                                }
                                this.background.img = img;

                                resolve();
                            });
                        } else {
                            axios
                                .post("/print/tiledimage", requestParams)
                                .then(response => {
                                    fabric.Image.fromURL(response.data, img => {
                                        if (
                                            img.width > this.settings.maxWidth
                                        ) {
                                            img.scaleToWidth(
                                                this.settings.maxWidth
                                            );
                                        }
                                        this.background.img = img;

                                        resolve();
                                    });
                                });
                        }
                    },
                    err => {
                        reject(err);
                    }
                );
        });
    }

    /**
     * Interpolate the feature collections.
     * Will only interpolate if the doInterpolation setting is true.
     * Fetches every image and then it passes the data on to createSingleInterpolation
     *
     * @param {function} callback
     */
    addInterpolations(callback) {
        if (!this.settings.doInterpolation) {
            this.images = [];
            this.createFinalGroup(this.featureCollection, callback);
            return;
        }

        for (let i = 0; i < this.featureCollection.features.length; i++) {
            this.loading = true;
            let geojson = this.featureCollection.features[i];

            // Need to transform the polygon to a multipolygon which the server can handle
            if (geojson.geometry.type != "MultiPolygon") {
                geojson.geometry.type = "MultiPolygon";
                geojson.geometry.coordinates = [geojson.geometry.coordinates];
            }

            let params = {
                geojson: JSON.stringify(geojson.geometry),
                type: this.settings.type,
                samples: this.samples.map(sample => {
                    return {
                        x: sample.x,
                        y: sample.y,
                        value: sample[this.settings.type]
                    };
                })
            };

            if (
                this.settings.interpolation_type != null &&
                this.settings.interpolation_type !=
                    this.settings.settingsObj.interpolation_type
            ) {
                params.interpolation_type = this.settings.interpolation_type;
            }

            if (
                this.settings.smooth_colors != null &&
                this.settings.smooth_colors !=
                    this.settings.settingsObj.smooth_colors
            ) {
                params.smooth_colors = this.settings.smooth_colors;
            }

            axios.post("/interpolate", params).then(response => {
                this.createSingleInterpolation(
                    response.data.imageUrl,
                    geojson,
                    this.featureCollection,
                    callback
                );
            });
        }
    }

    /**
     * Creates a fabric image based on the Base64 image URL and positions it correctly based on the featureCollection.
     * When it is creating the last fabric image it will call createFinalGroup
     *
     * @param {String} imageUrl
     * @param {GeoJson} geojson The current feature to be interpolated
     * @param {GeoJson} featureCollection All features of this layer
     * @param {function} callback
     */
    createSingleInterpolation(imageUrl, geojson, featureCollection, callback) {
        fabric.Image.fromURL(imageUrl, img => {
            let polybbox = bbox(geojson);
            if (!this.background) {
                this.bbox = polybbox;
            }

            const lowLeft = proj4("WGS84", "EPSG:3857", [
                polybbox[0],
                polybbox[1]
            ]);
            let minX = lowLeft[0];
            const topRight = proj4("WGS84", "EPSG:3857", [
                polybbox[2],
                polybbox[3]
            ]);
            let maxY = topRight[1];

            let adjustments = {
                top: parseInt(this.topRight[1] - maxY) * this.quality + 1, // Plus one because strange reasons
                left: parseInt(minX - this.lowLeft[0]) * this.quality + 1 // Plus one because strange reasons
            };

            img.set(adjustments);

            this.images.push(img);

            if (this.images.length === featureCollection.features.length) {
                this.createFinalGroup(featureCollection, callback);
            }
        });
    }

    /**
     * Create a group containing the objects that are created.
     * If there is interpolated images they will be included.
     * If there is a background image it will be included.
     * Will adjust the group based on the background image.
     *
     * @param {GeoJson} featureCollection
     * @param {function} callback
     */
    createFinalGroup(featureCollection, callback) {
        this.imagesGroup = new fabric.Group(this.images);

        if (this.adjustments) {
            // We have saved the needed adjustments since last time this was calculated. So lets continue
            callback();
            return;
        }

        let group = null;
        if (this.images.length) {
            group = new fabric.Group([
                this.imagesGroup,
                this.polygonGroup,
                this.text
            ]);
        } else {
            group = new fabric.Group([this.polygonGroup, this.text]);
        }

        if (this.background) {
            let adjustments = this.adjustInterpolationToFitBackground(
                featureCollection
            );

            this.scaleBy = adjustments.width / this.imagesGroup.width;
            this.adjustments = {
                top: adjustments.top,
                left: adjustments.left
            };

            group.set({
                top: adjustments.top,
                left: adjustments.left,
                scaleX: this.scaleBy,
                scaleY: this.scaleBy
            });

            this.group = new fabric.Group([this.background.img, group]);
        } else {
            if (group.width > this.settings.maxWidth) {
                group.scaleToWidth(this.settings.maxWidth);
            }
            this.group = group;

            this.bbox = bbox(featureCollection);
        }

        this.loading = false;
        this.group.id = this.id;
        callback();
    }

    /**
     * Create the adjustments needed to place the interpolations in the correct position on top of the background image.
     *
     * @param {GeoJson} featureCollection
     */
    adjustInterpolationToFitBackground(featureCollection) {
        let imageWidth = this.background.img.width;
        let imageHeight = this.background.img.height;
        if (this.background.img.scaleX && this.background.img.scaleY) {
            imageWidth = imageWidth * this.background.img.scaleX;
            imageHeight = imageHeight * this.background.img.scaleY;
        }

        // Get the needed adjustments so the interpolated group can be placed correctly
        let topLeftTiles = proj4(
            "WGS84",
            "EPSG:3857",
            this.background.props.topLeft
        );
        let topLeftFeature = proj4("WGS84", "EPSG:3857", [
            this.featureCollectionBBOX[0],
            this.featureCollectionBBOX[3]
        ]);

        let bottomRightTiles = proj4(
            "WGS84",
            "EPSG:3857",
            this.background.props.bottomRight
        );
        let bottomRightFeature = proj4("WGS84", "EPSG:3857", [
            this.featureCollectionBBOX[2],
            this.featureCollectionBBOX[1]
        ]);

        // Top Left
        let diffTopX =
            (topLeftFeature[0] - topLeftTiles[0]) /
            (bottomRightTiles[0] - topLeftTiles[0]);
        let adjustInterpolationLeft = diffTopX * imageWidth;
        let diffTopY =
            (topLeftTiles[1] - topLeftFeature[1]) /
            (topLeftTiles[1] - bottomRightTiles[1]);
        let adjustInterpolationTop = diffTopY * imageHeight;

        // Bottom Right
        let diffBottomX =
            (bottomRightTiles[0] - bottomRightFeature[0]) /
            (bottomRightTiles[0] - topLeftTiles[0]);
        let adjustInterpolationRight = diffBottomX * imageWidth;
        let width =
            imageWidth - (adjustInterpolationRight + adjustInterpolationLeft);

        return {
            top: adjustInterpolationTop,
            left: adjustInterpolationLeft,
            width: width
        };
    }

    /**
     * Create a layer containing all the polygons in the feature collection
     *
     * @param {Json} featureCollection
     */
    createPolygons(featureCollection) {
        let polygons = [];
        featureCollection.features.forEach(feature => {
            let polygon = this.parsePolygonCoordinates(feature);

            polygon.forEach(vertices => {
                let poly = new fabric.Polygon(vertices, {
                    stroke: "black",
                    strokeWidth: 1.5,
                    opacity: 1.0,
                    fill: "rgba(0,0,0,0)"
                });

                polygons.push(poly);
            });
        });

        return new fabric.Group(polygons);
    }

    /**
     * Create a layer containing all the texts that are placed on the map.
     */
    createTextGroup() {
        let textItems = [];
        if (this.settings.showText) {
            this.samples
                .filter(sample => {
                    // Make sure there is a value present, otherwise omit the sample
                    // Or check if the current interpolation type is any of the three specified in the array
                    return (
                        sample.isInside &&
                        (sample[this.settings.type] ||
                            [
                                "kalk_50_procent_cao",
                                "magnesiumbehov",
                                "avkastningsminskning"
                            ].includes(this.settings.type))
                    );
                })
                .forEach(sample => {
                    let value = sample[this.settings.type];
                    if (this.settings.type == "k_mg_kvot") {
                        value = parseFloat(value).toFixed(1);
                    }

                    var text = new fabric.Text(value.toString(), {
                        fontSize: this.settings.fontSize,
                        top: sample.yImage + this.textTop,
                        left: sample.xImage + this.textLeft,
                        fontFamily: "Helvetica"
                    });

                    if (this.settings.useFontOutline) {
                        text.stroke = "white";
                        text.strokeWidth = 1;
                        text.fontWeight = 900;
                    }

                    let circle = new fabric.Circle({
                        top: sample.yImage,
                        left: sample.xImage,
                        radius: 1.5,
                        fill: "black"
                    });

                    textItems = textItems.concat([text, circle]);
                });
        }

        return new fabric.Group(textItems);
    }

    /**
     * Will return an array of arrays containing all the vertices of the polygon.
     * Each coordinate for the vertices will be converted to fit the image.
     *
     * @param {GeoJson} polygon The geojson polygon that should be parsed
     */
    parsePolygonCoordinates(polygon) {
        return polygon.geometry.coordinates.map(coordinates => {
            return coordinates.map(coordinate => {
                let coord = proj4(
                    "WGS84",
                    "EPSG:3857",
                    coordinate.slice(0, 2)
                ).map((val, index) => {
                    return index === 0
                        ? this.transformX(val)
                        : this.transformY(val);
                });
                return {
                    x: coord[0],
                    y: coord[1]
                };
            });
        });
    }

    /**
     * Will map the samples so that the data is correct
     *
     * @param {GeoJson} samples The geojson features that should be parsed
     */
    parseSamples(samples) {
        return samples.map(sample => {
            return {
                id: sample.properties.id,
                customer_id: sample.properties.customer_id,
                year: sample.properties.year,
                provnr: sample.properties.provnr,
                x: sample.properties.x,
                y: sample.properties.y,
                x_3021: sample.properties.x_3021,
                y_3021: sample.properties.y_3021,
                xImage: this.transformX(sample.properties.x),
                yImage: this.transformY(sample.properties.y),
                ph: sample.properties.ph,
                p_al: sample.properties.p_al,
                p_olsen: sample.properties.p_olsen,
                k_al: sample.properties.k_al,
                mg_al: sample.properties.mg_al,
                ca_al: sample.properties.ca_al,
                k_mg_kvot: sample.properties.k_mg_kvot,
                cd_hno3: sample.properties.cd_hno3,
                lerhalt: sample.properties.lerhalt,
                mullhalt: sample.properties.mullhalt,
                sand_grovmo: sample.properties.sand_grovmo,
                jordart: sample.properties.jordart,
                other_years_lerhalt: sample.properties.other_years_lerhalt,
                other_years_mullhalt: sample.properties.other_years_mullhalt,
                p_hcl: sample.properties.p_hcl,
                k_hcl: sample.properties.k_hcl,
                cu_hcl: sample.properties.cu_hcl,
                bor: sample.properties.bor,
                zn: sample.properties.zn,
                cu: sample.properties.cu,
                cr: sample.properties.cr,
                ni: sample.properties.ni,
                pb: sample.properties.pb,
                hg: sample.properties.hg,
                volymvikt: sample.properties.volymvikt,
                t_varde: sample.properties.t_varde,
                s_varde: sample.properties.s_varde,
                basmattnadsgrad: sample.properties.basmattnadsgrad,
                fin_lerhalt: sample.properties.fin_lerhalt,
                fe_al: sample.properties.fe_al,
                al_al: sample.properties.al_al,
                mineralkvave_kg_n_ha: sample.properties.mineralkvave_kg_n_ha,
                mineralkvave_no3_n: sample.properties.mineralkvave_no3_n,
                mineralkvave_nh4_n: sample.properties.mineralkvave_nh4_n,
                mineralkvave_djup: sample.properties.mineralkvave_djup,
                cystnematoder_potatis: sample.properties.cystnematoder_potatis,
                cystnematoder_betor: sample.properties.cystnematoder_betor,
                cystnematoder_spannmal:
                    sample.properties.cystnematoder_spannmal,
                ovrigt: sample.properties.ovrigt,
                kalkbehov: sample.properties.kalkbehov,
                kalk_50_procent_cao: 0,
                avkastningsminskning: 0,
                magnesiumbehov: 0,
                isInside: sample.properties.isInside
            };
        });
    }

    /**
     * Adjust the passed coordinate so it fits the image.
     *
     * @param {float} x Horisontal coordinate
     */
    transformX(x) {
        return parseInt(x * this.quality - this.minX * this.quality);
    }

    /**
     * Adjust the passed coordinate so it fits the image.
     *
     * @param {float} y Vertical coordinate
     */
    transformY(y) {
        return (
            this.height * this.quality -
            parseInt(y * this.quality - this.minY * this.quality)
        );
    }

    /**
     * Calculate the minimum and maximum values for X and Y by transforming the
     * WGS84 bounding box in to EPSG:3857 coordinate system.
     *
     * @param {GeoJson} featureCollection The collection of polygons that is to be interpolated
     */
    calculateBBOX(featureCollection) {
        this.featureCollectionBBOX = bbox(featureCollection);
        this.lowLeft = proj4("WGS84", "EPSG:3857", [
            this.featureCollectionBBOX[0],
            this.featureCollectionBBOX[1]
        ]);
        this.topRight = proj4("WGS84", "EPSG:3857", [
            this.featureCollectionBBOX[2],
            this.featureCollectionBBOX[3]
        ]);

        this.minX = this.lowLeft[0];
        this.minY = this.lowLeft[1];
        this.maxX = this.topRight[0];
        this.maxY = this.topRight[1];

        this.height = parseInt(this.maxY - this.minY);
        // this.width = parseInt(this.maxX - this.minX)
    }
}

export default InterpolatedLayer;
